import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {MutationObserver | null} */
  #otpObserver = null;

  /** @type {MutationObserver | null} */
  #otpMountObserver = null;

  /** @type {number} */
  #variantOtpSuppressionUntil = 0;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    window.addEventListener('click', this.#handleCheckoutClick, true);
    window.addEventListener('submit', this.#handleCheckoutSubmit, true);
    window.addEventListener('change', this.#handleVariantChange, true);
    this.#watchOtpOverlay();

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    window.removeEventListener('click', this.#handleCheckoutClick, true);
    window.removeEventListener('submit', this.#handleCheckoutSubmit, true);
    window.removeEventListener('change', this.#handleVariantChange, true);
    this.#otpObserver?.disconnect();
    this.#otpMountObserver?.disconnect();
  }

  /**
   * A native modal dialog is rendered in the browser's top layer, above normal
   * app overlays regardless of their z-index. Close the drawer during the
   * capture phase so checkout/OTP apps can open their modal above the page.
   * The click is not cancelled, so Shopify and the OTP app still receive it.
   *
   * @param {MouseEvent} event
   */
  #handleCheckoutClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const checkoutButton = target?.closest('#checkout, .cart__checkout-button');

    if (!checkoutButton?.closest('#cart-drawer')) return;

    this.#closeImmediately();
  };

  /**
   * Covers keyboard activation and checkout apps that work from the cart form's
   * submit event instead of the checkout button's click event.
   *
   * @param {SubmitEvent} event
   */
  #handleCheckoutSubmit = (event) => {
    const submitter = event.submitter instanceof Element ? event.submitter : null;

    if (!submitter?.matches('#cart-drawer #checkout, #cart-drawer .cart__checkout-button')) return;

    this.#closeImmediately();
  };

  /**
   * Authyo currently mistakes product-option changes for checkout intent. Mark
   * the next OTP opening caused by a real variant picker so it can be dismissed
   * without affecting Buy It Now or an intentional cart checkout.
   *
   * @param {Event} event
   */
  #handleVariantChange = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const isProductVariant =
      target?.closest('variant-picker') &&
      target.matches('input[data-option-value-id], input[name^="options"], select[name^="options"]');

    if (!isProductVariant) return;

    this.#variantOtpSuppressionUntil = Date.now() + 2000;
  };

  /**
   * Authyo can stop the checkout event before it reaches theme components.
   * Watching the app root gives us a reliable fallback: as soon as its overlay
   * becomes visible, remove the cart's native dialog from the browser top layer.
   */
  #watchOtpOverlay() {
    const otpRoot = document.querySelector('#authyo-widget-container, .authyo-widget-root');

    if (otpRoot) {
      this.#observeOtpRoot(otpRoot);
      return;
    }

    this.#otpMountObserver = new MutationObserver(() => {
      const mountedOtpRoot = document.querySelector('#authyo-widget-container, .authyo-widget-root');
      if (!mountedOtpRoot) return;

      this.#otpMountObserver?.disconnect();
      this.#otpMountObserver = null;
      this.#observeOtpRoot(mountedOtpRoot);
    });

    this.#otpMountObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  /** @param {Element} otpRoot */
  #observeOtpRoot(otpRoot) {
    const closeIfVisible = () => {
      const styles = window.getComputedStyle(otpRoot);
      const isVisible =
        !otpRoot.hasAttribute('hidden') &&
        otpRoot.getAttribute('aria-hidden') !== 'true' &&
        styles.display !== 'none' &&
        styles.visibility !== 'hidden' &&
        styles.opacity !== '0' &&
        otpRoot.getClientRects().length > 0;

      if (!isVisible) return;

      if (Date.now() <= this.#variantOtpSuppressionUntil) {
        this.#variantOtpSuppressionUntil = 0;
        this.#dismissOtp(otpRoot);
        return;
      }

      if (this.#themeDrawer?.isOpen) this.#closeImmediately();
    };

    this.#otpObserver = new MutationObserver(closeIfVisible);
    this.#otpObserver.observe(otpRoot, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'],
    });

    closeIfVisible();
  }

  /** @param {Element} otpRoot */
  #dismissOtp(otpRoot) {
    const closeButton = otpRoot.querySelector('#authyo-close-btn');

    if (closeButton instanceof HTMLElement) {
      closeButton.click();
      return;
    }

    if (otpRoot instanceof HTMLElement) otpRoot.style.setProperty('display', 'none', 'important');
  }

  /**
   * Calling ThemeDrawer.close() alone waits for its exit animation before the
   * native dialog leaves the browser top layer. Close the panel first so the
   * OTP overlay is uncovered without any delay, then let ThemeDrawer clean up
   * its state and focus handling.
   */
  #closeImmediately() {
    if (this.#dialog?.open) this.#dialog.close();

    this.#themeDrawer?.close();
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
